import Vue from "vue";
import VueRouter from "vue-router";

// I18n
import i18n from "@/i18n";

// Store
import store from "@/store";

// Utils
import axios from "@/utils/axios.js";
import Service from "@/utils/service.js";
import { setTimeZone, setLocale, setEmail } from "@/utils";

Vue.use(VueRouter);

const routes = [
  {
    path: "/report-cat",
    name: "report-CAT",
    meta: {
      code: "H01",
      customValid(route) {
        // 報告頁可允許未登入但網址帶有 token 的使用者進入
        const { token } = route.query;

        return Boolean(token);
      }
    },
    component: () =>
      import(/* webpackChunkName: "ReportCAT" */ "../pages/report/CAT.vue")
  },
  {
    path: "/report-cat2",
    name: "report-CAT2",
    meta: {
      code: "H01",
      customValid(route) {
        // 報告頁可允許未登入但網址帶有 token 的使用者進入
        const { token } = route.query;

        return Boolean(token);
      }
    },
    component: () =>
      import(/* webpackChunkName: "ReportCAT2" */ "../pages/report/CAT2.vue")
  },
  {
    path: "/report-video",
    name: "report-video",
    meta: {
      code: "V11",
      customValid(route) {
        // 報告頁可允許未登入但網址帶有 token 的使用者進入
        const { token } = route.query;

        return Boolean(token);
      }
    },
    component: () =>
      import(/* webpackChunkName: "ReportCAT" */ "../pages/report/Video.vue")
  },
  {
    path: "/email-editor",
    name: "custom-email-editor",
    component: () =>
      import(/* webpackChunkName: "EmailEditor" */ "../pages/EmailEditor.vue")
  },
  {
    path: "/login",
    component: () =>
      import(/* webpackChunkName: "Login" */ "../views/Login.vue"),
    meta: {
      allowAnonymous: true
    },
    children: [
      {
        path: "",
        name: "login",
        component: () =>
          import(
            /* webpackChunkName: "LoginForm" */ "../pages/login/Index.vue"
          ),
        meta: {
          allowAnonymous: true
        }
      },
      {
        path: "forget-password",
        name: "forget-password",
        component: () =>
          import(
            /* webpackChunkName: "ForgetPassword" */ "../pages/login/ForgetPassword.vue"
          ),
        meta: {
          allowAnonymous: true
        },
        alias: "/forget-password"
      },
      {
        path: "update-password",
        name: "update-password",
        meta: {
          customValid(route) {
            // 允許未登入但網址帶有 token 的使用者進入
            const { token } = route.query;

            return Boolean(token);
          }
        },
        component: () =>
          import(
            /* webpackChunkName: "UpdatePassword" */ "../pages/login/UpdatePassword.vue"
          ),
        alias: "/update-password"
      },
      {
        path: "mfa-auth",
        name: "mfa-auth",
        meta: {
          customValid() {
            // 允許未登入但擁有 authorization code 的 user 進入
            const authorization_code = store.getters["auth/authorization_code"];
            return Boolean(authorization_code);
          }
        },
        component: () => import("../pages/login/MfaAuth.vue"),

        alias: "/mfa-auth"
      }
    ]
  },
  {
    path: "/",
    component: () => import(/* webpackChunkName: "Home" */ "../views/Home.vue"),
    children: [
      {
        path: "",
        name: "/",
        meta: {
          name: "/" // 針對 sidebar active 判斷
        },
        component: () =>
          import(/* webpackChunkName: "Landing" */ "../pages/Landing.vue")
      },
      {
        path: "privacy",
        name: "privacy",
        component: () =>
          import(/* webpackChunkName: "Privacy" */ "../pages/Privacy.vue"),
        meta: {
          allowAnonymous: true
        }
      },
      {
        path: "recruit-cat",
        name: "recruit-cat",
        meta: {
          name: "recruit-cat",
          code: "G01"
        },
        component: () =>
          import(
            /* webpackChunkName: "RecruitCat" */ "../pages/recruit/Cat.vue"
          )
      },
      {
        path: "recruit-cat-batch",
        name: "recruit-cat-batch",
        meta: {
          name: "recruit-cat",
          code: "G02"
        },
        component: () =>
          import(
            /* webpackChunkName: "RecruitCatBatch" */ "../pages/recruit/CatBatch.vue"
          )
      },
      {
        path: "recruit-ddi",
        name: "recruit-ddi",
        meta: {
          name: "recruit-ddi",
          code: "G11"
        },
        component: () =>
          import(
            /* webpackChunkName: "RecruitDdi" */ "../pages/recruit/Ddi.vue"
          )
      },
      {
        path: "recruit-ddi-batch",
        name: "recruit-ddi-batch",
        meta: {
          name: "recruit-ddi",
          code: "G12"
        },
        component: () =>
          import(
            /* webpackChunkName: "RecruitDdiBatch" */ "../pages/recruit/DdiBatch.vue"
          )
      },
      {
        path: "recruit-harrison",
        name: "recruit-harrison",
        meta: {
          name: "recruit-harrison",
          code: "G21"
        },
        component: () =>
          import(
            /* webpackChunkName: "RecruitHarrison" */ "../pages/recruit/Harrison.vue"
          )
      },
      {
        path: "recruit-harrison-batch",
        name: "recruit-harrison-batch",
        meta: {
          name: "recruit-harrison",
          code: "G22"
        },
        component: () =>
          import(
            /* webpackChunkName: "RecruitHarrisonBatch" */ "../pages/recruit/HarrisonBatch.vue"
          )
      },
      {
        path: "vacancy-invite",
        name: "vacancy-invite",
        meta: {
          name: "vacancy-invite",
          code: "J01"
        },
        component: () =>
          import(
            /* webpackChunkName: "VacancyInvite" */ "../pages/vacancy/Invite.vue"
          )
      },
      {
        path: "vacancy-subject",
        name: "vacancy-subject",
        meta: {
          name: "vacancy-subject",
          code: "J11"
        },
        component: () =>
          import(
            /* webpackChunkName: "VacancySubject" */ "../pages/vacancy/Subject.vue"
          )
      },
      {
        path: "vacancy-subject-batch",
        name: "vacancy-subject-batch",
        meta: {
          name: "vacancy-subject",
          code: "J12"
        },
        component: () =>
          import(
            /* webpackChunkName: "VacancySubjectBatch" */ "../pages/vacancy/SubjectBatch.vue"
          )
      },
      {
        path: "vacancy-subject-bank",
        name: "vacancy-subject-bank",
        meta: {
          name: "vacancy-subject",
          code: "J12"
        },
        component: () =>
          import(
            /* webpackChunkName: "VacancySubjectBank" */ "../pages/vacancy/SubjectBank.vue"
          )
      },
      {
        path: "video-exam",
        name: "video-exam",
        meta: {
          name: "video-exam",
          code: "V01"
        },
        component: () =>
          import(/* webpackChunkName: "VideoExam" */ "../pages/video/Exam.vue")
      },
      {
        path: "video-question",
        name: "video-question",
        meta: {
          name: "video-exam",
          code: "V01"
        },
        component: () =>
          import(
            /* webpackChunkName: "VideoQuestion" */ "../pages/video/Question.vue"
          )
      },
      {
        path: "enterprise-department",
        name: "enterprise-department",
        meta: {
          name: "enterprise-department",
          code: "F01"
        },
        component: () =>
          import(
            /* webpackChunkName: "EnterpriseDepartment" */ "../pages/enterprise/Department.vue"
          )
      },
      {
        path: "enterprise-role",
        name: "enterprise-role",
        meta: {
          name: "enterprise-role",
          code: "E01"
        },
        component: () =>
          import(
            /* webpackChunkName: "EnterpriseRole" */ "../pages/enterprise/Role.vue"
          )
      },
      {
        path: "enterprise-account",
        name: "enterprise-account",
        meta: {
          name: "enterprise-account"
          // code: "D01"
        },
        component: () =>
          import(
            /* webpackChunkName: "EnterpriseAccount" */ "../pages/enterprise/Account.vue"
          )
      },
      {
        path: "enterprise-account-batch",
        name: "enterprise-account-batch",
        meta: {
          name: "enterprise-account",
          code: "D02"
        },
        component: () =>
          import(
            /* webpackChunkName: "EnterpriseAccountBatch" */ "../pages/enterprise/AccountBatch.vue"
          )
      },
      {
        path: "enterprise-system",
        name: "enterprise-system",
        meta: {
          name: "enterprise-system",
          code: "C01"
        },
        component: () =>
          import(
            /* webpackChunkName: "EnterpriseSystem" */ "../pages/enterprise/System.vue"
          )
      },
      {
        path: "enterprise-quota",
        name: "enterprise-quota",
        meta: {
          name: "enterprise-quota",
          code: "Quota.Read"
        },
        component: () =>
          import(
            /* webpackChunkName: "EnterpriseQuota" */ "../pages/enterprise/Quota.vue"
          )
      },
      {
        path: "enterprise-quota-detail/:type",
        name: "enterprise-quota-detail/:type",
        meta: {
          name: "enterprise-quota",
          code: "Quota.Read"
        },
        component: () =>
          import(
            /* webpackChunkName: "EnterpriseQuotaDetail" */ "../pages/enterprise/QuotaDetail.vue"
          )
      }
    ]
  }
];

const router = new VueRouter({
  mode: "history",
  base: process.env.BASE_URL,
  routes
});

router.beforeEach(async (to, from, next) => {
  let operations = store.getters["auth/operations"];
  const isLoggedIn = store.getters["auth/isLoggedIn"];

  if (operations.length === 0 && isLoggedIn) {
    try {
      const service = new Service(axios);
      const { Results } = await service.getAccountDetail();
      const { Operations, TimeZone, LangCode, Email } = Results[0];

      setTimeZone(TimeZone);
      setLocale(LangCode);
      setEmail(Email);

      i18n.locale = LangCode;
      document.documentElement.setAttribute("lang", LangCode.split("-")[0]);

      operations = Operations;

      store.dispatch("auth/updateOperations", operations);
    } catch (error) {
      console.error(error);
    }
  }

  const name = to.name;
  const code = to.meta?.code;
  const allowAnonymous = to.meta?.allowAnonymous;
  const customValid = to.meta?.customValid;

  if (name == "login" && isLoggedIn) {
    next({ path: "/" });
  } else if (!allowAnonymous && !isLoggedIn) {
    if (!customValid || !customValid(to)) {
      next({ name: "login" });
    } else {
      next();
    }
  } else if (code && (!operations || !operations.includes(code))) {
    const { path } = from;
    next({ path });
  } else {
    next();
  }
});

export default router;
